/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/shipment/list/filters.tsx — its
 * `FilterElements`: one field per filter, inline on a wide toolbar with the label hidden,
 * full-width and labelled inside the drawer.
 *
 * Adapted for server-side entity-type filtering: the entity-type `Input` writes the route's search
 * params (the loader re-fetches on every navigation) rather than a TanStack column filter. Status is
 * the status dropdown on the toolbar (Queue.tsx), not a drawer field — one control per filter. The
 * toolbar's search box stays client-side (TanStack's own global filter) because it searches only the
 * window already fetched (up to 200 rows), not a service parameter.
 */
import { useNavigate } from "react-router-dom";
import type { Table as ReactTableType } from "@tanstack/react-table";
import { Input } from "rizzui";
import { FILTER_LABEL } from "../../../components/table/toolbar";
import type { SyncQueueItem } from "../../../lib/services/tallySync";

export function Filters({
  table,
  entityType,
  query,
}: {
  table: ReactTableType<SyncQueueItem>;
  entityType?: string;
  query: (over: Record<string, string | undefined>) => string;
}) {
  const navigate = useNavigate();
  // A filter change is a different result set; a leftover search term typed against the old one would
  // silently hide rows in it, so it goes too.
  const apply = (next: string | undefined) => {
    table.setGlobalFilter("");
    navigate(query({ type: next }));
  };

  // Applied on Enter or leaving the field, not per keystroke: each apply is a refetch. Keyed on the
  // URL value so Clear / Back resets what the box shows.
  return (
    <Input
      key={entityType ?? ""}
      label="Entity type"
      labelClassName={FILTER_LABEL}
      placeholder="Entity type, then Enter"
      title="e.g. invoice, credit_note — press Enter to apply"
      defaultValue={entityType ?? ""}
      inputClassName="h-9"
      className="w-full @5xl:w-56"
      onKeyDown={(event) => {
        if (event.key === "Enter") apply(event.currentTarget.value.trim() || undefined);
      }}
      onBlur={(event) => {
        const next = event.currentTarget.value.trim() || undefined;
        if (next !== entityType) apply(next);
      }}
    />
  );
}
